import { validateScenePlanReplay } from '@/_pure/episode';
import { loadEpisodeCatalog } from '@/_pure/episodeCatalog';
import { resolveEpisodeExcerpt } from '@/_pure/episodeExcerpt';
import episodeAuthoring from '@/adapters/primitive/_data/developerEpisodes/episode-authoring.json' with { type: 'json' };
import catalog from '@/adapters/primitive/_data/episodes/_@shared/catalog.json' with { type: 'json' };
import booleanWheel from '@/adapters/primitive/_data/episodes/_@sub_boolean/boolean-wheel.json' with { type: 'json' };
import logicalAnd from '@/adapters/primitive/_data/episodes/_@sub_boolean/logical-and.json' with { type: 'json' };
import logicalNot from '@/adapters/primitive/_data/episodes/_@sub_boolean/logical-not.json' with { type: 'json' };
import logicalOr from '@/adapters/primitive/_data/episodes/_@sub_boolean/logical-or.json' with { type: 'json' };
import logicalXOr from '@/adapters/primitive/_data/episodes/_@sub_boolean/logical-xor.json' with { type: 'json' };
import failingDivision from '@/adapters/primitive/_data/episodes/_@sub_natural/failing-division.json' with { type: 'json' };
import failingSubtraction from '@/adapters/primitive/_data/episodes/_@sub_natural/failing-subtraction.json' with { type: 'json' };
import naturalAdd from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-add.json' with { type: 'json' };
import naturalAddWithCarry from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-add-with-carry.json' with { type: 'json' };
import divide from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-divide.json' with { type: 'json' };
import comparison from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-less-than.json' with { type: 'json' };
import multiply from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-multiply.json' with { type: 'json' };
import naturalSubtract from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-subtract.json' with { type: 'json' };
import naturalSubtractWithDeficit from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-subtract-with-deficit.json' with { type: 'json' };
import naturalWheel from '@/adapters/primitive/_data/episodes/_@sub_natural/natural-wheel.json' with { type: 'json' };
import operandScheduling from '@/adapters/primitive/_data/episodes/_@sub_natural/operand-scheduling.json' with { type: 'json' };
import settlement from '@/adapters/primitive/_data/episodes/_@sub_natural/alignment-and-settlement.json' with { type: 'json' };
import helperWheelsAndValidEndStates from '@/adapters/primitive/_data/episodes/_@sub_natural/helper-wheels-and-valid-end-states.json' with { type: 'json' };
import unitAndRadixBands from '@/adapters/primitive/_data/episodes/_@sub_natural/unit-and-radix-bands.json' with { type: 'json' };
import identity from '@/adapters/primitive/_data/episodes/identity.json' with { type: 'json' };
import { withQuotientGrowthIllustration } from '@/adapters/primitive/_pure/derivation/natural/quotientGrowthIllustration';
import { validatePrimitiveEpisodeTargets } from '@/adapters/primitive/_pure/derivation/episodeGuidance';
import { buildPrimitiveRodFrame } from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { configForPrimitiveTrace, derivePrimitiveTraceReplay, projectPrimitiveTracePayload } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import { resolvePrimitiveSceneDirection } from '@/adapters/primitive/_pure/derivation/sceneDirection';
import { parsePrimitiveEpisode } from '@/adapters/primitive/_pure/episode';
import { resolveAuditionEpisode } from '@/adapters/primitive/scenePlanAuditions';
import { SPACE_KEY } from '@/adapters/primitive/spaceIdentity';
import { resolvePrimitiveTrace } from '@/adapters/primitive/traceCatalog';
import { primitiveVisualSources } from '@/adapters/primitive/visualSourceCatalog';
import type { Config } from '@/adapters/primitive/_contract/config';
import type { PrimitiveSceneDirection, PrimitiveSceneDirectionCatalogs } from '@/adapters/primitive/_contract/sceneDirection';
import type { PrimitiveScenePlan as ScenePlan } from '@/adapters/primitive/_contract/scenePlan';
import type { PrimitiveTrace } from '@/adapters/primitive/_contract/trace';

const documents = new Map<string, unknown>([
    ['identity', identity],
    ['_@sub_natural/natural-wheel', naturalWheel],
    ['_@sub_boolean/boolean-wheel', booleanWheel],
    ['_@sub_boolean/logical-and', logicalAnd], ['_@sub_boolean/logical-or', logicalOr],
    ['_@sub_boolean/logical-xor', logicalXOr],
    ['_@sub_boolean/logical-not', logicalNot],
    ['_@sub_natural/natural-add', naturalAdd], ['_@sub_natural/natural-subtract', naturalSubtract],
    ['_@sub_natural/natural-add-with-carry', naturalAddWithCarry],
    ['_@sub_natural/natural-subtract-with-deficit', naturalSubtractWithDeficit],
    ['_@sub_natural/unit-and-radix-bands', unitAndRadixBands],
    ['_@sub_natural/alignment-and-settlement', settlement],
    ['_@sub_natural/helper-wheels-and-valid-end-states', helperWheelsAndValidEndStates],
    ['_@sub_natural/operand-scheduling', operandScheduling],
    ['_@sub_natural/natural-multiply', multiply], ['_@sub_natural/natural-divide', divide],
    ['_@sub_natural/natural-less-than', comparison],
    ['_@sub_natural/failing-subtraction', failingSubtraction], ['_@sub_natural/failing-division', failingDivision],
]);
export const primitiveEpisodes = loadEpisodeCatalog('Primitive', catalog, documents,
    value => parsePrimitiveEpisode(value, primitiveVisualSources));

export const primitiveDeveloperEpisodes = [parsePrimitiveEpisode(episodeAuthoring, primitiveVisualSources)];

export function resolvePrimitiveEpisode(handle: string, sceneIndex = 0) {
    const episode = primitiveEpisodes.find((episode) => episode.handle === handle)
        ?? (process.env.NODE_ENV === 'development' ? primitiveDeveloperEpisodes.find(episode => episode.handle === handle) : undefined)
        ?? resolveAuditionEpisode(handle);
    if (!episode) throw new Error(`Primitive Episode '${handle}' is unavailable.`);
    const scene = episode.scenes[sceneIndex];
    if (!scene) throw new Error(`Primitive Episode '${handle}' has no scene ${sceneIndex}.`);
    return { episode, scene };
}

export function createPrimitiveScenePlanResolver() {
    const cacheByConfig = new WeakMap<Config, { trace: PrimitiveTrace;
        direction?: PrimitiveSceneDirection;
        directions?: PrimitiveSceneDirectionCatalogs;
        divisionComparison?: 'explicit' | 'integrated';
        fullComparisons?: number;
        illustrativeQuotientBands?: number;
        replay: ReturnType<typeof derivePrimitiveTraceReplay>; steps: ReturnType<typeof projectPrimitiveTracePayload>;
        validated: Set<string> }>();
    return (config: Config, handle: string, sceneIndex = 0) => {
        const { episode, scene } = resolvePrimitiveEpisode(handle, sceneIndex);
        if (scene.sceneKind !== 'trace') throw new Error('Concept scenes have no execution trace.');
        const plan: ScenePlan = { schemaVersion: 1, title: scene.title, trace: scene.trace, beats: scene.beats };
        const trace = resolvePrimitiveTrace(plan.trace.handle);
        let cache = cacheByConfig.get(config);
        if (cache?.trace !== trace || cache.divisionComparison !== scene.divisionComparison
            || cache.fullComparisons !== scene.fullComparisons
            || cache.illustrativeQuotientBands !== scene.illustrativeQuotientBands || cache.direction !== scene.direction) {
            const effective = configForPrimitiveTrace(config, trace);
            const directions = scene.direction ? resolvePrimitiveSceneDirection(scene.direction) : undefined;
            const steps = withQuotientGrowthIllustration(effective,
                projectPrimitiveTracePayload(effective, trace.payload, true, scene.divisionComparison, directions?.visuals,
                    scene.fullComparisons), scene.illustrativeQuotientBands, scene.fullComparisons);
            cache = { trace, divisionComparison: scene.divisionComparison, illustrativeQuotientBands: scene.illustrativeQuotientBands,
                fullComparisons: scene.fullComparisons,
                direction: scene.direction, directions,
                replay: derivePrimitiveTraceReplay(trace, steps), steps, validated: new Set() };
            cacheByConfig.set(config, cache);
        }
        validateScenePlanReplay(plan, cache.replay);
        const excerpt = resolveEpisodeExcerpt(scene.excerpt, plan, cache.replay);
        const openingStepIndex = excerpt?.from ?? 0;
        const validationKey = `${handle}/${scene.id}`;
        if (plan.beats.some(beat => beat.presentation) && !cache.validated.has(validationKey)) {
            const effective = configForPrimitiveTrace(config, trace);
            const steps = cache.steps;
            validatePrimitiveEpisodeTargets(plan, cache.replay, steps, index => buildPrimitiveRodFrame({
                spaceKey: SPACE_KEY, config: effective, wheels: steps[index]!.keyframes.at(-1)!.wheels,
                booleanRod: steps[index]!.keyframes.at(-1)!.booleanRod,
            }), effective.base.display.emphasis, openingStepIndex);
            cache.validated.add(validationKey);
        }
        return { episode, scene, plan, trace, replay: cache.replay, steps: cache.steps, openingStepIndex, excerpt, directions: cache.directions };
    };
}